import React, { useState, useMemo, useEffect } from "react";
import {
  Alert, Box, Paper, Typography, Button, CircularProgress, IconButton, Chip, Avatar,
  TextField, InputAdornment, MenuItem, Divider, Tooltip,
  Drawer, Snackbar, Checkbox, FormControlLabel, List, ListItem,
  ListItemText, ListItemAvatar, Grid, Stack
} from "@mui/material";
import {
  Search as SearchIcon, Person as PersonIcon, People as PeopleIcon, Add as AddIcon,
  Edit as EditIcon, Email as EmailIcon, Phone as PhoneIcon,
  Info as InfoIcon, Close as CloseIcon, Comment as CommentIcon, Star as StarIcon, StarBorder as StarBorderIcon,
  AssignmentInd as AssignmentIndIcon
} from "@mui/icons-material";
import { blue, orange, grey } from "@mui/material/colors";
import { useNavigate } from 'react-router-dom';

// --- Import the refactored Intake Wizard ---
import IntakeWizardUnified from "../components/IntakeWizardUnified";
import { getCurrentUser, listTeam } from '../features/account/api';
import { matterTypeFor } from '../edition';
import { CLIENT_TYPES } from '../data/clientTypes';

// --------- Mock Data, Constants ---------
const API_BASE = process.env.REACT_APP_API_BASE || "";
const TAGS = ["VIP", "Individual", "Business", "Insurer", "Public body", "Litigation guardian", "Accessibility", "Urgent relief", "Limitation risk", "Settlement", "Indigenous", "Repeat", "Youth", "Mental Health", "Domestic", "Bail Risk", "Victim", "Witness"];
const STATUSES = ["Active", "Closed", "Intake", "Hold", "Conflicted", "Disqualified"];
const CLIENT_TYPES_LABEL = value => CLIENT_TYPES.find(type => type.id === value)?.label || '';
// Client data is fetched from the backend

const text = value => String(value ?? '');

export function normalizeClient(value) {
  const client = value?.client || value || {};
  const displayName = text(client.name || client.clientName || `${text(client.firstName)} ${text(client.lastName)}`).trim() || 'Unnamed client';
  const [fallbackFirst, ...fallbackLast] = displayName.split(/\s+/);
  return {
    ...client,
    name: displayName,
    firstName: text(client.firstName || fallbackFirst || 'Unnamed'),
    lastName: text(client.lastName || fallbackLast.join(' ')),
    email: text(client.email),
    phone: text(client.phone),
    status: text(client.status || 'Intake'),
    created: text(client.created || client.createdAt).slice(0, 10),
    tags: Array.isArray(client.tags) ? client.tags : [],
    matters: Array.isArray(client.matters) ? client.matters : [],
    assigned: client.assigned && typeof client.assigned === 'object' ? client.assigned : null,
  };
}

function filterClients(clients, q, filters) {
  return clients.filter(client => {
    let ok = true;
    if (q) {
      const needle = q.toLowerCase();
      ok =
        text(client.name).toLowerCase().includes(needle) ||
        text(client.firstName).toLowerCase().includes(needle) ||
        text(client.lastName).toLowerCase().includes(needle) ||
        text(client.email).toLowerCase().includes(needle) ||
        (client.tags || []).some(tag => text(tag).toLowerCase().includes(needle));
    }
    if (filters.status) ok = ok && client.status === filters.status;
    if (filters.tag) ok = ok && (client.tags || []).includes(filters.tag);
    if (filters.assigned) ok = ok && client.assigned?.name === filters.assigned;
    if (filters.starred) ok = ok && client.starred;
    return ok;
  });
}

// ---------- Main Exported Component ----------
export default function ClientIntakeCanadianCriminal() {
  const navigate = useNavigate();
  // UI State
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState("created-desc");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showIntake, setShowIntake] = useState(false);
  const [snack, setSnack] = useState("");

  // Data
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [clientTotal, setClientTotal] = useState(0);
  const [clientLimit, setClientLimit] = useState(200);
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const filteredClients = useMemo(() => {
    let arr = filterClients(clients, query, filters);
    if (sortBy === "created-desc") arr = arr.slice().sort((a, b) => text(b.created).localeCompare(text(a.created)));
    if (sortBy === "created-asc") arr = arr.slice().sort((a, b) => text(a.created).localeCompare(text(b.created)));
    if (sortBy === "name") arr = arr.slice().sort((a, b) => text(a.name).localeCompare(text(b.name)));
    return arr;
  }, [clients, query, filters, sortBy]);

  useEffect(() => {
    Promise.all([listTeam().catch(() => ({ team: [] })), getCurrentUser().catch(() => null)])
      .then(([teamBody, meBody]) => {
      const members = teamBody.team || [];
      if (members.length) setTeam(members);
      else if (meBody?.user) setTeam([meBody.user]);
    }).catch(err => console.error('Failed to load the firm team', err));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoadingClients(true);
      setLoadError('');
      const params = new URLSearchParams({ limit: String(clientLimit), sort: sortBy });
      if (query.trim()) params.set('q', query.trim());
      if (filters.status) params.set('status', filters.status);
      if (filters.tag) params.set('tag', filters.tag);
      if (filters.assigned) params.set('assigned', filters.assigned);
      if (filters.starred) params.set('starred', 'true');
      fetch(`${API_BASE}/api/clients?${params}`, { signal: controller.signal })
        .then(res => {
          if (!res.ok) throw new Error('Unable to load clients.');
          const rawTotal = res.headers.get('X-Total-Count');
          const headerTotal = rawTotal == null ? null : Number(rawTotal);
          return res.json().then(body => ({ body, headerTotal }));
        })
        .then(({ body, headerTotal }) => {
          const list = Array.isArray(body) ? body : body.clients;
          if (!Array.isArray(list)) throw new Error('The client list returned an invalid response.');
          const normalized = list.map(normalizeClient);
          setClients(normalized);
          setClientTotal(Number.isFinite(headerTotal) ? headerTotal : Number(body.total || normalized.length));
        })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.error('Failed to load clients', err);
            setLoadError(err.message || 'Unable to load clients.');
          }
        })
        .finally(() => { if (!controller.signal.aborted) setLoadingClients(false); });
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, filters, sortBy, clientLimit, reloadToken]);

  // ---- RENDER ----
  return (
    <Box sx={{ display: "flex", height: "100%", minHeight: 0, bgcolor: "background.default" }}>
      {/* ---------- MAIN CONTENT ----------- */}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {/* ---- HEADER ---- */}
        <Box sx={{
          display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 1.25, md: 2 },
          bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider", position: "sticky", top: 0, zIndex: 2
        }}>
          <PeopleIcon sx={{ color: blue[700], fontSize: { xs: 26, md: 32 }, flexShrink: 0 }} />
          <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.55rem', md: '2.125rem' }, lineHeight: 1.1 }}>Client Management</Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowIntake(true)}
            sx={{ whiteSpace: 'nowrap' }}
          ><Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>New Client</Box><Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>New</Box></Button>
          <Tooltip title="Open the canonical Matter Copilot workspace">
            <IconButton onClick={() => navigate('/copilot')}>
              <CommentIcon sx={{ color: blue[600] }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ---- FILTER/SORT BAR ---- */}
        <Box sx={{ display: "flex", alignItems: "center", px: { xs: 1.5, sm: 2, md: 3 }, pt: 2, pb: 0, gap: 1.25, flexWrap: "wrap" }}>
          <TextField
            placeholder="Search name, tags, email…"
            size="small"
            value={query}
            onChange={e => setQuery(e.target.value)}
            sx={{ width: { xs: '100%', sm: 260 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <TextField
            select size="small" label="Status" value={filters.status || ""}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value || undefined }))}
            sx={{ width: { xs: 'calc(50% - 5px)', sm: 135 } }}
          >
            <MenuItem value="">Any Status</MenuItem>
            {STATUSES.map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Tag" value={filters.tag || ""}
            onChange={e => setFilters(f => ({ ...f, tag: e.target.value || undefined }))}
            sx={{ width: { xs: 'calc(50% - 5px)', sm: 135 } }}
          >
            <MenuItem value="">Any Tag</MenuItem>
            {TAGS.map(tag => <MenuItem key={tag} value={tag}>{tag}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Assigned" value={filters.assigned || ""}
            onChange={e => setFilters(f => ({ ...f, assigned: e.target.value || undefined }))}
            sx={{ width: { xs: 'calc(50% - 5px)', sm: 170 } }}
          >
            <MenuItem value="">Anyone</MenuItem>
            {team.map(u => <MenuItem key={u.id} value={u.name}>{u.name}</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!filters.starred}
                onChange={e => setFilters(f => ({ ...f, starred: e.target.checked || undefined }))}
                sx={{ color: orange[500] }}
              />
            }
            label="Starred"
            sx={{ ml: { xs: 0, sm: 0.5 } }}
          />
          <TextField
            select size="small" label="Sort By" value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            sx={{ width: { xs: 'calc(50% - 5px)', sm: 135 }, ml: { xs: 0, sm: 0.5 } }}
          >
            <MenuItem value="created-desc">Newest</MenuItem>
            <MenuItem value="created-asc">Oldest</MenuItem>
            <MenuItem value="name">Name</MenuItem>
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}><Typography variant="body2" color="text.secondary">Showing {filteredClients.length} of {clientTotal} clients</Typography>{clients.length < clientTotal && <Button size="small" onClick={() => setClientLimit(limit => limit + 200)}>Load more</Button>}</Stack>
        </Box>

        {/* ---- CLIENT LIST ---- */}
        <Box sx={{ flex: 1, overflow: "auto", p: { xs: 1.5, sm: 2, md: 3 }, pt: 1 }}>
          {loadError && <Alert severity="error" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={() => { setLoadingClients(true); setLoadError(''); setReloadToken(value => value + 1); }}>Try again</Button>}>{loadError}</Alert>}
          <Paper sx={{ minHeight: 380, borderRadius: 2, overflow: "hidden" }}>
              {loadingClients ? <Box sx={{ minHeight: 380, display: 'grid', placeItems: 'center' }}><CircularProgress aria-label="Loading clients" /></Box> : <ClientTable
                clients={filteredClients}
                onSelect={c => { setSelected(c); setDrawerOpen(true); }}
                onOpenMatter={matterId => navigate(`/matters?id=${encodeURIComponent(matterId)}`)}
                onStar={cid => {
                  const client = clients.find(c => c.id === cid);
                  if (!client) return;
                  const updated = { ...client, starred: !client.starred };
                  fetch(`${API_BASE}/api/clients/${cid}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ starred: updated.starred })
                  }).catch(err => console.error('Failed to update star', err));
                  setClients(cs => cs.map(c => c.id === cid ? updated : c));
                }}
              />}
          </Paper>
        </Box>
      </Box>

      {/* ---------- CLIENT DRAWER ----------- */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 540 }, maxWidth: '100vw', bgcolor: "background.paper" } }}
      >
          <ClientDrawer
            key={selected?.id || 'closed'}
            client={selected}
            team={team}
            onClose={() => setDrawerOpen(false)}
            onOpenMatter={matterId => {
              setDrawerOpen(false);
              navigate(`/matters?id=${encodeURIComponent(matterId)}`);
            }}
            onSave={newMeta => {
              if (selected && newMeta) {
                fetch(`${API_BASE}/api/clients/${selected.id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(newMeta)
                }).catch(err => console.error('Failed to update client', err));
                setClients(cs => cs.map(c => c.id === selected.id ? { ...c, ...newMeta } : c));
              }
              setSnack("Client updated.");
              setDrawerOpen(false);
            }}
          />
      </Drawer>

      {/* ------ INTAKE WIZARD IN DRAWER ------- */}
      <Drawer
        anchor="right"
        open={showIntake}
        onClose={() => setShowIntake(false)}
        PaperProps={{ sx: { width: { xs: "100vw", sm: 760, lg: 1120 }, maxWidth: '100vw', bgcolor: "background.paper" } }}
      >
          <IntakeWizardUnified
            onClose={() => setShowIntake(false)}
            professionals={team.filter(person => ['admin', 'lawyer', 'paralegal'].includes(person.role))}
            onSubmit={data => {
              const activeRetainer = data.representationStatus === 'retained' && data.retainerStatus === 'signed';
              const isCivil = data.matterType === 'civil';
              const matterTitle = isCivil
                ? (data.matterTitle || `${data.clientName || 'Client'} civil matter`)
                : (data.charges || []).map(ch => ch.charge).filter(Boolean).join(", ") || "New Matter";
              const matter = {
                id: Date.now(),
                title: matterTitle,
                status: activeRetainer ? "Active" : "Intake",
                court: data.court || data.offenceProvince || data.jurisdiction,
                openDate: new Date().toISOString().slice(0,10),
                fileNum: data.fileNumbers || "",
                notes: [data.allegationSummary || data.disputeSummary, data.notes].filter(Boolean)
              };
              const [firstName, ...lastParts] = (data.clientName || "New").trim().split(/\s+/);
              const payload = {
                name: text(data.clientName).trim() || 'New client',
                clientName: text(data.clientName).trim() || 'New client',
                firstName: firstName || "New",
                lastName: lastParts.join(" "),
                preferredName: data.preferredName,
                email: data.clientEmail,
                phone: data.clientPhone,
                relationship: data.relationship,
                status: activeRetainer ? 'Active' : 'Intake',
                created: new Date().toISOString().slice(0,10),
                assigned: team.find(person => person.name === data.responsibleProfessional) || { name: data.responsibleProfessional || 'Unassigned', avatar: text(data.responsibleProfessional).slice(0, 1).toUpperCase() },
                tags: isCivil
                  ? [data.clientType?.includes('individual') ? 'Individual' : 'Business', data.urgentRelief ? 'Urgent relief' : '', !data.limitationVerified ? 'Limitation risk' : ''].filter(Boolean)
                  : [data.inCustody === 'yes' ? 'Bail Risk' : '', data.indigenous ? 'Indigenous' : '', data.youth ? 'Youth' : '', data.mentalHealth ? 'Mental Health' : ''].filter(Boolean),
                matters: [matter],
                timeline: [{ date: new Date().toISOString(), event: 'Conflict-cleared intake completed' }],
                notes: data.notes ? [data.notes] : [],
                intake: data,
                readiness: data.readiness,
                conflictCheck: data.conflictCheck,
                identityVerification: data.identityVerification,
                consents: data.consents,
              };
              fetch(`${API_BASE}/api/matters`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  title: matter.title,
                  clientName: data.clientName,
                  matterType: data.matterType,
                  practiceArea: isCivil ? 'Civil dispute' : 'Criminal and regulatory',
                  jurisdiction: data.offenceProvince || data.jurisdiction,
                  court: data.court || '',
                  objectives: data.clientObjectives,
                  nextCourtDate: data.nextCourtDate,
                  fileNumber: data.fileNumbers,
                  assignedTo: data.responsibleProfessional,
                  stage: 'intake-scope',
                  status: activeRetainer ? 'active' : 'intake',
                  intake: data,
                })
              })
                .then(res => {
                  if (!res.ok) return res.json().then(body => { throw new Error(body.error || 'Failed to create matter'); });
                  return res.json();
                })
                .then(({ matter: privateMatter }) => fetch(`${API_BASE}/api/clients`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    ...payload,
                    matters: [{ ...matter, id: privateMatter.id, matterNumber: privateMatter.matterNumber }],
                  })
                }))
                .then(res => {
                  if (!res.ok) return res.json().then(body => { throw new Error(body.error || 'Failed to create client'); });
                  return res.json();
                })
                .then(body => {
                  const newClient = normalizeClient(body);
                  setClients(cs => [newClient, ...cs]);
                  setClientTotal(total => total + 1);
                  setShowIntake(false);
                  setSnack("Client intake and private matter created.");
                })
                .catch(err => {
                  console.error('Failed to add client', err);
                  setSnack(err.message || 'Failed to add client.');
                });
            }}
          />
      </Drawer>

      <Snackbar
        open={!!snack}
        autoHideDuration={2000}
        onClose={() => setSnack("")}
        message={snack}
      />
    </Box>
  );
}

// -------- Client Table (unchanged) --------
function ClientTable({ clients, onSelect, onStar, onOpenMatter }) {
  if (!clients.length)
    return <Box sx={{ p: 6, textAlign: "center", color: grey[600] }}>No clients match your filters.</Box>;
  return (
    <Box sx={{ width: "100%", overflow: "auto" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "36px 1.3fr 1fr 1fr 100px 70px 60px",
          bgcolor: "#f7f8fa", borderBottom: "1px solid #e0e0e0", fontWeight: 700, fontSize: 16
        }}
      >
        <Box sx={{ p: 2 }} />
        <Box sx={{ p: 2 }}>Name</Box>
        <Box sx={{ p: 2 }}>Tags</Box>
        <Box sx={{ p: 2 }}>Assigned</Box>
        <Box sx={{ p: 2 }}>Status</Box>
        <Box sx={{ p: 2 }}>Created</Box>
        <Box sx={{ p: 2 }} />
      </Box>
      {clients.map(client => (
        <Box
          key={client.id}
          sx={{
            display: "grid", alignItems: "center",
            gridTemplateColumns: "36px 1.3fr 1fr 1fr 100px 70px 60px",
            borderBottom: "1px solid #f0f1f2", bgcolor: "#fff",
            "&:hover": { bgcolor: "#f4f7ff", cursor: "pointer" }
          }}
        >
          <Box sx={{ px: 2 }}>
            <IconButton onClick={e => { e.stopPropagation(); onStar(client.id); }}>
              {client.starred ? <StarIcon sx={{ color: orange[500] }} /> : <StarBorderIcon sx={{ color: grey[400] }} />}
            </IconButton>
          </Box>
          <Box sx={{ py: 2, pl: 2, fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}
            onClick={() => onSelect(client)}
          >
            <PersonIcon sx={{ color: blue[500], mr: 1 }} />
            {client.firstName} {client.lastName}
          </Box>
          <Box sx={{ py: 2 }}>
            {client.tags?.slice(0, 2).map(t =>
              <Chip key={t} label={t} size="small" sx={{ mr: 0.5, mb: 0.5, bgcolor: blue[50] }} />
            )}
            {(client.tags?.length > 2) && (
              <Chip label={`+${client.tags.length - 2}`} size="small" sx={{ bgcolor: blue[50] }} />
            )}
          </Box>
          <Box sx={{ py: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar sx={{ width: 24, height: 24, bgcolor: blue[200] }}>{client.assigned?.avatar || client.assigned?.name?.split(/\s+/).map(part => part[0]).join('').slice(0, 2).toUpperCase()}</Avatar>
            {client.assigned?.name}
          </Box>
          <Box sx={{ py: 2 }}>
            <Chip label={client.status} size="small"
              color={
                client.status === "Active" ? "success"
                  : client.status === "Intake" ? "primary"
                    : client.status === "Closed" ? "default"
                      : client.status === "Hold" ? "warning"
                        : client.status === "Disqualified" ? "error"
                          : "default"
              }
              sx={{ fontWeight: 700, fontSize: 13 }}
            />
          </Box>
          <Box sx={{ py: 2 }}>{client.created}</Box>
          <Box sx={{ px: 1, display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={e => {
                e.stopPropagation();
                const matterId = client.matters?.[0]?.id;
                if (matterId) onOpenMatter(matterId);
              }}
              disabled={!client.matters?.[0]?.id}
            >
              Open Matter
            </Button>
            <Tooltip title="Open">
              <IconButton onClick={() => onSelect(client)}>
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// --------- Client Drawer (unchanged) --------
function ClientDrawer({ client, team, onClose, onSave, onOpenMatter }) {
  const [meta, setMeta] = useState(client || {});
  const [editMode, setEditMode] = useState(false);
  if (!client) return null;
  const handleMetaChange = (k, v) => setMeta(m => ({ ...m, [k]: v }));
  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2, bgcolor: blue[700], color: "#fff", display: "flex", alignItems: "center" }}>
        <PersonIcon sx={{ mr: 1 }} />
        <Typography variant="h6" fontWeight={700}>
          {client.firstName || "New"} {client.lastName || "Client"}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={editMode ? "Cancel Edit" : "Edit"}>
          <IconButton color="inherit" onClick={() => setEditMode(e => !e)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton color="inherit" onClick={onClose}><CloseIcon /></IconButton>
        </Tooltip>
      </Box>
      <Divider />
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary">Contact & Assignment</Typography>
            </Grid>
            <Grid size={7}>
              {editMode ? (
                <>
                  <TextField label="First Name" value={meta.firstName || ""} onChange={e => handleMetaChange("firstName", e.target.value)} size="small" fullWidth sx={{ mb: 1 }} />
                  <TextField label="Last Name" value={meta.lastName || ""} onChange={e => handleMetaChange("lastName", e.target.value)} size="small" fullWidth sx={{ mb: 1 }} />
                  <TextField label="Email" value={meta.email || ""} onChange={e => handleMetaChange("email", e.target.value)} size="small" fullWidth sx={{ mb: 1 }} />
                  <TextField label="Phone" value={meta.phone || ""} onChange={e => handleMetaChange("phone", e.target.value)} size="small" fullWidth sx={{ mb: 1 }} />
                  <TextField label="Relationship" value={meta.relationship || ""} onChange={e => handleMetaChange("relationship", e.target.value)} size="small" fullWidth sx={{ mb: 1 }} />
                  <TextField label="Gender" value={meta.gender || ""} onChange={e => handleMetaChange("gender", e.target.value)} size="small" fullWidth sx={{ mb: 1 }} />
                </>
              ) : (
                <List dense>
                  <ListItem><ListItemAvatar><Avatar><EmailIcon /></Avatar></ListItemAvatar><ListItemText primary="Email" secondary={client.email} /></ListItem>
                  <ListItem><ListItemAvatar><Avatar><PhoneIcon /></Avatar></ListItemAvatar><ListItemText primary="Phone" secondary={client.phone} /></ListItem>
                  {(client.relationship || client.intake?.relationship) && (
                    <ListItem>
                      <ListItemAvatar><Avatar><AssignmentIndIcon /></Avatar></ListItemAvatar>
                      <ListItemText primary="Relationship" secondary={client.relationship || client.intake?.relationship} />
                    </ListItem>
                  )}
                  {(client.gender || client.intake?.gender) && (
                    <ListItem>
                      <ListItemAvatar><Avatar><PersonIcon /></Avatar></ListItemAvatar>
                      <ListItemText primary="Gender" secondary={client.gender || client.intake?.gender} />
                    </ListItem>
                  )}
                </List>
              )}
            </Grid>
            <Grid size={5}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Responsible professional</Typography>
              {editMode ? (
                <TextField
                  select size="small" fullWidth label="Assigned"
                  value={meta.assigned?.name || ""}
                  onChange={e =>
                    handleMetaChange("assigned", team.find(u => u.name === e.target.value))
                  }
                >
                  {team.map(u => <MenuItem key={u.id} value={u.name}>{u.name}</MenuItem>)}
                </TextField>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Avatar sx={{ mr: 1, bgcolor: blue[200] }}>{client.assigned?.avatar || client.assigned?.name?.split(/\s+/).map(part => part[0]).join('').slice(0, 2).toUpperCase()}</Avatar>
                  {client.assigned?.name}
                </Box>
              )}
            </Grid>
            <Grid size={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid size={6}>
              <Typography variant="subtitle2">Status</Typography>
              {editMode ? (
                <TextField
                  select size="small" fullWidth
                  value={meta.status || ""}
                  onChange={e => handleMetaChange("status", e.target.value)}
                >
                  {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              ) : (
                <Chip label={client.status} color={
                  client.status === "Active" ? "success"
                    : client.status === "Intake" ? "primary"
                      : client.status === "Closed" ? "default"
                        : client.status === "Hold" ? "warning"
                          : client.status === "Disqualified" ? "error"
                            : "default"
                } />
              )}
            </Grid>
            <Grid size={6}>
              <Typography variant="subtitle2">Tags</Typography>
              {editMode ? (
                <TextField
                  select size="small" fullWidth label="Tags"
                  value={meta.tags || []}
                  onChange={e => handleMetaChange("tags", typeof e.target.value === "string" ? [e.target.value] : e.target.value)}
                  SelectProps={{ multiple: true }}
                >{TAGS.map(tag => <MenuItem key={tag} value={tag}>{tag}</MenuItem>)}</TextField>
              ) : (
                <>
                  {client.tags?.map(t =>
                    <Chip key={t} label={t} size="small" sx={{ mr: 0.5, mb: 0.5, bgcolor: blue[50] }} />
                  )}
                </>
              )}
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, color: "text.secondary" }}>Created</Typography>
              <Typography variant="body2">{client.created}</Typography>
            </Grid>
            {client.intake && (
              <>
                <Grid size={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">Personal Details</Typography>
                  <Typography variant="body2">
                    {CLIENT_TYPES_LABEL(client.intake.clientType) || client.intake.relationship}
                    {client.intake.gender ? ` • ${client.intake.gender}` : ''}
                    {client.intake.clientDOB ? ` • DOB: ${client.intake.clientDOB}` : ''}
                  </Typography>
                  {client.intake.citizenship && (
                    <Typography variant="body2">Citizenship: {client.intake.citizenship}</Typography>
                  )}
                  {client.intake.language && (
                    <Typography variant="body2">Language: {client.intake.language}</Typography>
                  )}
                  {client.intake.ethnicity && (
                    <Typography variant="body2">Ethnicity: {client.intake.ethnicity}</Typography>
                  )}
                  {client.intake.address && (
                    <Typography variant="body2">Address: {client.intake.address}</Typography>
                  )}
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Background</Typography>
                  {client.intake.immigrationStatus && (
                    <Typography variant="body2">Immigration: {client.intake.immigrationStatus}</Typography>
                  )}
                  {client.intake.employment && (
                    <Typography variant="body2">Employment: {client.intake.employment}</Typography>
                  )}
                  {client.intake.dependents && (
                    <Typography variant="body2">Dependents: {client.intake.dependents}</Typography>
                  )}
                </Grid>
                {matterTypeFor(client) === 'civil' && <>
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Authority & representative</Typography>
                    <Typography variant="body2">{client.intake.instructingPerson || 'No separate representative'}{client.intake.instructingPersonTitle ? ` · ${client.intake.instructingPersonTitle}` : ''}</Typography>
                    <Typography variant="body2">{client.intake.authorityBasis || 'Authority basis pending'}</Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Civil dispute</Typography>
                    <Typography variant="body2">{client.intake.jurisdiction} · {client.intake.court} · {client.intake.proceedingType}</Typography>
                    <Typography variant="body2">{client.intake.disputeSummary}</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={.5} sx={{ mt: .75 }}>{(client.intake.claims || []).map(claim => <Chip key={claim} size="small" label={claim} />)}</Stack>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Limitations & preservation</Typography>
                    <Typography variant="body2">Possible limitation: {client.intake.limitationDate || 'not recorded'} · verified: {client.intake.limitationVerified ? 'yes' : 'no'}</Typography>
                    <Typography variant="body2">{client.intake.preservationActions || 'Preservation action not recorded'}</Typography>
                  </Grid>
                </>}
                {matterTypeFor(client) === 'criminal' && <>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">Offence Details</Typography>
                  <Typography variant="body2">
                    {client.intake.offenceDate} • {client.intake.offenceProvince}
                    {client.intake.offenceLocation ? ` • ${client.intake.offenceLocation}` : ''}
                  </Typography>
                  {client.intake.policeAgency && (
                    <Typography variant="body2">Police: {client.intake.policeAgency}</Typography>
                  )}
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Charges</Typography>
                  {(client.intake.charges || []).map((ch, i) => (
                    <Chip
                      key={i}
                      size="small"
                      label={`${ch.charge}${ch.statute ? ` (${ch.statute})` : ''}`}
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Record</Typography>
                  {client.intake.criminalHistory && (
                    <Typography variant="body2">{client.intake.criminalHistory}</Typography>
                  )}
                  {client.intake.outstandingMatters && (
                    <Typography variant="body2">Outstanding: {client.intake.outstandingMatters}</Typography>
                  )}
                </Grid>
                </>}
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Objectives</Typography>
                  {client.intake.clientObjectives && (
                    <Typography variant="body2">{client.intake.clientObjectives}</Typography>
                  )}
                </Grid>
              </>
            )}
            {editMode && (
              <Grid size={12}>
                <Box sx={{ mt: 2 }}>
                  <Button variant="contained" color="primary" onClick={() => onSave(meta)}>Save</Button>
                  <Button sx={{ ml: 1 }} onClick={() => setEditMode(false)}>Cancel</Button>
                </Box>
              </Grid>
            )}
            <Grid size={12}>
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" color="primary" disabled={!client.matters?.[0]?.id} onClick={() => onOpenMatter(client.matters[0].id)}>
                  Continue in matter workspace
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Evidence, facts, notes, issues, timeline, workflow, and audit history are maintained in the matter workspace.
                </Typography>
              </Box>
            </Grid>
          </Grid>
      </Box>
    </Box>
  );
}
